import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpStatusCode, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TimeUtil } from '@shared/utils/time.util';
import { Observable, of, throwError } from 'rxjs';
import { retryWhen, concatMap, delay } from 'rxjs/operators';

/**
 * A general handler that will, by default, retry all HTTP requests that fail due to an unknown error.
 */
@Injectable({
  providedIn: 'root',
})
export class RetryHttpInterceptor implements HttpInterceptor {
  /**
   * The number of attempts we'll make at a request, beyond the original attempt.
   *
   * E.g. if this is set to 3 and the initial request fails, we'll retry up to 3 further times for 4 total requests.
   * If this is set to 0, we won't retry requests at all.
   */
  private readonly RETRY_LIMIT = 5;
  /**
   * The wait time in milliseconds between each error and the next attempt.
   */
  private readonly RETRY_WAIT_MS = TimeUtil.toMilliseconds({ seconds: 2 });

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      retryWhen(error =>
        error.pipe(
          concatMap((error, count) => {
            if (error instanceof HttpErrorResponse) {
              const retryStatus = !error.status || error.status === HttpStatusCode.GatewayTimeout;
              if (count <= this.RETRY_LIMIT && retryStatus) {
                // Retry
                return of(error);
              }
            }
            // Give up
            return throwError(error);
          }),
          delay(this.RETRY_WAIT_MS),
        )
      )
    );
  }

}
